import { useAppSelector } from 'hooks/useStore'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Theme } from 'theme/theme'
import { componentByMonth } from './dashboardFigures.helpers'
import DownloadButton from 'components/DownloadButton'
import { InvoiceClass, InvoiceComptaStatus } from '@Arti-zen/package-backoffice'
import { fetchAllPages } from 'utils/async'
import { ApiArtizenBO } from 'utils/artizenConnector'
import { Button } from '@mui/material'
import { Format } from 'utils/format'

export default function DashboardInvoicesFigures(): ReactElement {
  const { dateStart, dateEnd } = useAppSelector((state) => state.dashboard)
  const [invoices, setInvoices] = useState<InvoiceClass['meilisearchDoc'][]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [needRefresh, setNeedRefresh] = useState(true)

  useEffect(() => {
    setNeedRefresh(true)
  }, [dateStart, dateEnd])

  const refresh = () => {
    setIsLoading(true)
    setNeedRefresh(false)

    fetchAllPages(
      ApiArtizenBO.invoices.get,
      {
        startDate: dateStart.toJSDate(),
        endDate: dateEnd.toJSDate(),
        status: [InvoiceComptaStatus.Paid, InvoiceComptaStatus.Unpaid],
      },
      { chunkSize: 1000 }
    )
      .then(setInvoices)
      .finally(() => setIsLoading(false))
  }

  type TStats = Record<number, Record<number, { count: number; totalHtCts: number }>>

  const stats = useMemo<TStats>(() => {
    return invoices.reduce((acc, inv) => {
      if (inv.state !== 'DONE') return acc
      const d = new Date(inv.creationDate)

      const year = d.getFullYear()
      const month = d.getMonth() + 1 // 1-12

      if (!acc[year]) {
        acc[year] = {}
      }

      if (!acc[year][month]) {
        acc[year][month] = {
          count: 0,
          totalHtCts: 0,
        }
      }

      acc[year][month].count += 1
      acc[year][month].totalHtCts += inv.totalHT_cts

      return acc
    }, {} as TStats)
  }, [invoices])

  const downloadSummaryCsv = () => {
    const summary: Record<
      string,
      { companyID: string; month: string; count: number; totalHTcts: number; totalTTCcts: number }
    > = {}

    invoices.forEach((inv) => {
      if (inv.state !== 'DONE') return
      const d = new Date(inv.creationDate)
      const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const companyID = inv.ref?.match(/(\d+)_/)?.[1] // "1243"
      const key = `${companyID}|${month}`
      console.log({ companyID, month, key, inv })

      if (!summary[key]) {
        summary[key] = { companyID, month, count: 0, totalHTcts: 0, totalTTCcts: 0 }
      }
      summary[key].count += 1
      summary[key].totalHTcts += inv.totalHT_cts
      summary[key].totalTTCcts += inv.totalTTC_cts
    })

    const rows = Object.values(summary).sort(
      (a, b) => a.companyID.localeCompare(b.companyID) || a.month.localeCompare(b.month)
    )

    const escape = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`
    const csv = [
      ['companyID', 'mois', 'nombre_factures', 'montant_HT', 'montant_TTC'].map(escape).join(';'),
      ...rows.map((r) =>
        [
          r.companyID,
          r.month,
          r.count,
          (r.totalHTcts / 100).toFixed(2).replace('.', ','),
          (r.totalTTCcts / 100).toFixed(2).replace('.', ','),
        ]
          .map(escape)
          .join(';')
      ),
    ].join('\n')

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'invoices_summary_by_company.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  if (needRefresh) {
    return (
      <>
        <Button variant="contained" onClick={refresh}>
          Actualiser
        </Button>
      </>
    )
  }
  return isLoading ? (
    <>Chargment en cours ...</>
  ) : (
    <>
      <td
        style={{
          fontWeight: 600,
          color: Theme.colors.blueArtizen,
          display: 'flex',
          flexDirection: 'row',
          gap: 10,
          alignItems: 'center',
        }}
      >
        Factures
        {!!invoices && <DownloadButton data={invoices} fileName="invoices" label={null} size="small" />}
        {!!invoices.length && (
          <Button variant="outlined" size="small" onClick={downloadSummaryCsv}>
            Résumé par société (CSV)
          </Button>
        )}
      </td>
      {!stats ? null : (
        <>
          <tr>
            <td style={{ textAlign: 'right' }}>Nombre</td>
            {componentByMonth({
              dateStart,
              dateEnd,
              component: (year, month) => (
                <td style={{ padding: 10, textAlign: 'center' }}>
                  <div>{Format.number(stats[year]?.[month]?.count || 0)}</div>
                </td>
              ),
            })}
          </tr>
          <tr>
            <td style={{ textAlign: 'right' }}>TOTAL HT</td>
            {componentByMonth({
              dateStart,
              dateEnd,
              component: (year, month) => (
                <td style={{ padding: 10, textAlign: 'center' }}>
                  <div>{Format.currency((stats[year]?.[month]?.totalHtCts || 0) / 100)}</div>
                </td>
              ),
            })}
          </tr>
        </>
      )}
    </>
  )
}
